import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type FormEvent,
  type InputHTMLAttributes,
} from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { useAuth } from '@/hooks/useAuth'
import {
  fetchInstitutionDonorDetail,
  isFieldMissing,
  updateInstitutionDonor,
  sendInstitutionDonorPasswordReset,
} from '@/lib/institution-donors'
import { formatCurrency } from '@/lib/campaign-dashboard'
import { ApiClientError } from '@/lib/api/client'
import { CpfCnpjInput, onlyDigits } from '@/components/CpfCnpjInput'
import { DonorAvatar } from '@/features/donorAvatar/DonorAvatar'
import { parseAvatarConfig } from '@/features/donorAvatar/avatar-catalog'
import { buildWhatsAppChatHref } from '@/lib/whatsapp-link'
import {
  DonorLuckyNumbersSection,
  DonorAchievementsSection,
  DonorQuestionnaireSection,
} from '@/components/Clientes'
import type {
  InstitutionDonorDetail,
  InstitutionDonorUpdatePayload,
} from '@/types/institution-donor'
import styles from './EditarClientePage.module.css'

function cx(...parts: (string | false | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

type TextFieldProps = {
  id: string
  label: string
  name: string
  defaultValue: string | null | undefined
  placeholderWhenMissing?: string
  fieldClassName?: string
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'defaultValue'>

function TextField({
  id,
  label,
  name,
  defaultValue,
  placeholderWhenMissing = 'Sem informação',
  fieldClassName,
  className,
  ...rest
}: TextFieldProps) {
  const missing = isFieldMissing(defaultValue)
  return (
    <div className={cx(styles.field, fieldClassName)}>
      <label className={cx(styles.label, missing && styles.labelMissing)} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        name={name}
        defaultValue={defaultValue ?? ''}
        placeholder={missing ? placeholderWhenMissing : undefined}
        className={cx(styles.input, missing && styles.inputMissing, className)}
        {...rest}
      />
    </div>
  )
}

export function EditarClientePage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { token, guard, isCollaborator } = useAuth()
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false)
  const [passwordResetMessage, setPasswordResetMessage] = useState<string | null>(null)
  const [passwordResetError, setPasswordResetError] = useState<string | null>(null)
  const [data, setData] = useState<InstitutionDonorDetail | null>(null)
  const canManageGlobalIdentity = data?.can_manage_global_identity ?? guard === 'admin'
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null)
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false)
  const [formVersion, setFormVersion] = useState(0)
  const [documentDigits, setDocumentDigits] = useState('')

  const donorId = id ? Number.parseInt(id, 10) : NaN

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!Number.isFinite(donorId)) {
        setError('Identificador do cliente inválido.')
        setData(null)
        setIsLoading(false)
        return
      }
      if (!opts?.silent) {
        setIsLoading(true)
        setError(null)
      }
      try {
        const result = await fetchInstitutionDonorDetail(donorId, token)
        setData(result)
      } catch (e) {
        if (!opts?.silent) {
          setData(null)
          if (e instanceof ApiClientError) {
            setError(
              e.status === 404 ? 'Cliente não encontrado nesta instituição.' : e.message
            )
          } else {
            setError('Não foi possível carregar os dados do cliente.')
          }
        }
      } finally {
        if (!opts?.silent) {
          setIsLoading(false)
        }
      }
    },
    [donorId, token]
  )

  useEffect(() => {
    void load()
  }, [load])

  useLayoutEffect(() => {
    if (!data) return
    if (!isFieldMissing(data.document_cpf)) {
      setDocumentDigits(onlyDigits(data.document_cpf ?? ''))
    } else if (!isFieldMissing(data.document_cnpj)) {
      setDocumentDigits(onlyDigits(data.document_cnpj ?? ''))
    } else {
      setDocumentDigits('')
    }
  }, [data])

  const buildUpdatePayload = (
    fd: FormData,
    detail: InstitutionDonorDetail,
    documentRaw: string
  ): InstitutionDonorUpdatePayload => {
    const str = (name: string) => String(fd.get(name) ?? '').trim()
    const strOrNull = (name: string) => {
      const v = str(name)
      return v === '' ? null : v
    }
    const phoneRaw = str('phone')
    const phone = phoneRaw === '' ? null : phoneRaw.replace(/\D/g, '') || null
    const passwordRaw = String(fd.get('password') ?? '')
    const payload: InstitutionDonorUpdatePayload = {
      first_name: str('first_name'),
      last_name: str('last_name'),
      ...(detail.person_type === 'pj' ? { corporate_name: str('corporate_name') } : {}),
      email: str('email'),
      phone,
      document: documentRaw === '' ? null : documentRaw,
      website: strOrNull('website'),
      address: {
        street: strOrNull('street'),
        city: strOrNull('city'),
        state: strOrNull('state'),
        postal_code: strOrNull('postal_code'),
        neighborhood: strOrNull('neighborhood'),
        number: strOrNull('number'),
        complement: strOrNull('complement'),
      },
    }
    if (passwordRaw !== '') {
      payload.password = passwordRaw
    }
    return payload
  }

  const handleSaveProfile = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!data || !Number.isFinite(donorId)) return
    const form = e.currentTarget
    setProfileSaveError(null)
    setProfileSaveSuccess(false)
    setIsSavingProfile(true)
    try {
      const fd = new FormData(form)
      const payload = buildUpdatePayload(fd, data, documentDigits)
      const updated = await updateInstitutionDonor(donorId, payload, token)
      setData(updated)
      setFormVersion((version) => version + 1)
      setProfileSaveSuccess(true)
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 422 && err.errors) {
          const flat = Object.values(err.errors).flat()
          setProfileSaveError(flat[0] ?? err.message ?? 'Verifique os dados informados.')
        } else {
          setProfileSaveError(err.message)
        }
      } else {
        setProfileSaveError('Não foi possível salvar as alterações.')
      }
    } finally {
      setIsSavingProfile(false)
    }
  }

  const documentMissing = documentDigits.length === 0

  const handlePasswordReset = async () => {
    setSendingPasswordReset(true)
    setPasswordResetMessage(null)
    setPasswordResetError(null)
    try {
      setPasswordResetMessage(await sendInstitutionDonorPasswordReset(donorId))
    } catch (err) {
      setPasswordResetError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível enviar a recuperação de acesso.'
      )
    } finally {
      setSendingPasswordReset(false)
    }
  }

  const emailMissing = data ? isFieldMissing(data.email) : false
  const questionnaireAnswers = data?.questionnaire?.answers ?? []
  const showQuestionnaire = questionnaireAnswers.length > 0
  const avatar = data ? parseAvatarConfig(data.avatar) : null
  const whatsappHref = data?.phone ? buildWhatsAppChatHref(data.phone) : null

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <h1 className={styles.title}>Ficha do cliente</h1>
        <nav className={styles.breadcrumbs} aria-label="Navegação estrutural">
          <Link className={styles.breadcrumbLink} to="/home">
            Home
          </Link>
          <span className={styles.breadcrumbSep}>/</span>
          <Link className={styles.breadcrumbLink} to="/clientes/lista">
            Clientes
          </Link>
          {id ? (
            <>
              <span className={styles.breadcrumbSep}>/</span>
              <span className={styles.breadcrumbCurrent}>#{id}</span>
            </>
          ) : null}
        </nav>
      </div>

      {error ? (
        <p className={styles.loadError} role="alert">
          {error}
        </p>
      ) : null}

      {profileSaveError ? (
        <p className={styles.loadError} role="alert">
          {profileSaveError}
        </p>
      ) : null}
      {profileSaveSuccess ? (
        <p className={styles.saveSuccess} role="status">
          Dados do cliente atualizados com sucesso.
        </p>
      ) : null}

      {isLoading ? <p className={styles.loading}>Carregando dados do cliente…</p> : null}

      {!isLoading && data ? (
        <>
          <section className={styles.hero} aria-label="Resumo do cliente">
            <div className={styles.heroAvatar}>
              <Icon icon="mdi:account-outline" aria-hidden />
              {avatar ? (
                <DonorAvatar
                  config={avatar}
                  portrait
                  label={`Avatar de ${data.display_name}`}
                />
              ) : data.profile_photo_url ? (
                <img
                  src={data.profile_photo_url}
                  alt={`Foto de ${data.display_name}`}
                  onError={(event) => {
                    event.currentTarget.hidden = true
                  }}
                />
              ) : null}
            </div>
            <div className={styles.heroIdentity}>
              <span className={styles.heroEyebrow}>Ficha do cliente · #{data.id}</span>
              <h2 className={styles.heroName}>{data.display_name}</h2>
              <div className={styles.heroMeta}>
                <span className={emailMissing ? styles.textMissing : undefined}>
                  <Icon icon="mdi:email-outline" aria-hidden />{' '}
                  {emailMissing ? 'E-mail não informado' : data.email}
                </span>
                {whatsappHref ? (
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer">
                    <Icon icon="mdi:whatsapp" aria-hidden /> {data.phone}
                  </a>
                ) : null}
              </div>
            </div>
            <a className={styles.heroEdit} href="#cliente-dados">
              <Icon icon="mdi:pencil-outline" aria-hidden /> Editar dados
            </a>
          </section>

          <section className={styles.summaryGrid} aria-label="Indicadores do cliente">
            <div className={styles.metric}>
              <span>Valor gasto</span>
              <strong>{formatCurrency(data.stats.total_paid_amount)}</strong>
              <small>Compras pagas</small>
            </div>
            <div className={styles.metric}>
              <span>Pedidos pagos</span>
              <strong>{data.stats.paid_orders_count}</strong>
              <small>Na instituição</small>
            </div>
            <div className={styles.metric}>
              <span>Números da sorte</span>
              <strong>{data.stats.assigned_numbers_count}</strong>
              <small>Atribuídos</small>
            </div>
            <div className={styles.metric}>
              <span>Campanhas</span>
              <strong>{data.stats.campaigns_count}</strong>
              <small>Com compra paga</small>
            </div>
          </section>

          <div className={styles.layout}>
            <div className={styles.mainColumn}>
              <div className={`${styles.card} ${styles.numbersCard}`}>
                <DonorLuckyNumbersSection
                  key={data.id}
                  donorId={data.id}
                  token={token}
                  total={data.stats.assigned_numbers_count}
                  canCancel={
                    !isCollaborator && (guard === 'institution' || guard === 'admin')
                  }
                  onOrderCanceled={() => void load({ silent: true })}
                />
              </div>
              <div className={styles.card}>
                <DonorAchievementsSection
                  key={`${data.id}:${formVersion}:${data.stats.paid_orders_count}:${data.stats.total_paid_amount}`}
                  donorId={data.id}
                  token={token}
                />
              </div>
              {showQuestionnaire ? (
                <section
                  className={`${styles.card} ${styles.questionnaireCard}`}
                  aria-label="Respostas do questionário pós-pagamento"
                >
                  <DonorQuestionnaireSection answers={questionnaireAnswers} />
                </section>
              ) : null}
            </div>

            <div className={`${styles.card} ${styles.formCard}`} id="cliente-dados">
              <div className={styles.formIntro}>
                <span className={styles.heroEyebrow}>Cadastro</span>
                <h2>Dados do cliente</h2>
                <p>
                  Atualize as informações de contato e endereço. As alterações são salvas
                  nesta ficha.
                </p>
                {profileSaveSuccess ? (
                  <p className={styles.formSuccess} role="status">
                    Alterações salvas.
                  </p>
                ) : null}
              </div>
              <form
                key={`${data.id}-${formVersion}`}
                onSubmit={(ev) => {
                  void handleSaveProfile(ev)
                }}
                aria-busy={isSavingProfile}
              >
                <section className={styles.formSection} aria-labelledby="sec-pessoal">
                  <h3 id="sec-pessoal" className={styles.sectionHeading}>
                    {data.person_type === 'pj'
                      ? 'Identificação da empresa'
                      : 'Dados pessoais'}
                  </h3>
                  <div className={styles.fieldGrid}>
                    {data.person_type === 'pj' ? (
                      <TextField
                        id="ec-corporate"
                        label="Razão social"
                        name="corporate_name"
                        defaultValue={data.corporate_name}
                        fieldClassName={styles.fieldFull}
                        required
                      />
                    ) : (
                      <>
                        <TextField
                          id="ec-first"
                          label="Primeiro nome"
                          name="first_name"
                          defaultValue={data.first_name}
                          autoComplete="given-name"
                        />
                        <TextField
                          id="ec-last"
                          label="Sobrenome"
                          name="last_name"
                          defaultValue={data.last_name}
                          autoComplete="family-name"
                        />
                      </>
                    )}
                    <TextField
                      id="ec-email"
                      label="E-mail"
                      name="email"
                      type="email"
                      defaultValue={data.email}
                      autoComplete="email"
                      readOnly={!canManageGlobalIdentity}
                      required={canManageGlobalIdentity}
                    />
                    <TextField
                      id="ec-phone"
                      label="WhatsApp / telefone"
                      name="phone"
                      type="tel"
                      defaultValue={data.phone}
                      autoComplete="tel"
                      readOnly={!canManageGlobalIdentity}
                    />
                    <div className={styles.field}>
                      <label
                        className={cx(
                          styles.label,
                          documentMissing && styles.labelMissing
                        )}
                        htmlFor="ec-cpf"
                      >
                        {data.person_type === 'pj' ? 'CNPJ' : 'CPF'}
                      </label>
                      <CpfCnpjInput
                        id="ec-cpf"
                        mode={data.person_type === 'pj' ? 'cnpj' : 'cpf'}
                        value={documentDigits}
                        onChange={(_masked, raw) => setDocumentDigits(raw)}
                        className={cx(
                          styles.input,
                          documentMissing && styles.inputMissing
                        )}
                        autoComplete="off"
                        disabled={isSavingProfile || !canManageGlobalIdentity}
                      />
                    </div>
                    <TextField
                      id="ec-website"
                      label="Website"
                      name="website"
                      type="url"
                      defaultValue={data.website}
                      placeholderWhenMissing="https://"
                      autoComplete="url"
                    />
                  </div>
                </section>

                <section className={styles.formSection} aria-labelledby="sec-seguranca">
                  <h3 id="sec-seguranca" className={styles.sectionHeading}>
                    Acesso à conta
                  </h3>
                  {canManageGlobalIdentity ? (
                    <div className={styles.fieldGrid}>
                      <div className={`${styles.field} ${styles.fieldFull}`}>
                        <label className={styles.label} htmlFor="ec-password">
                          Senha
                        </label>
                        <input
                          id="ec-password"
                          className={styles.input}
                          name="password"
                          type="password"
                          placeholder="Deixe em branco para manter a senha atual"
                          autoComplete="new-password"
                        />
                        {!data.has_password ? (
                          <p className={styles.hintMissing}>
                            Este cliente ainda não cadastrou uma senha.
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <>
                      <p>
                        O comprador gerencia seu e-mail, WhatsApp, documento e senha na
                        conta Sortee.
                      </p>
                      <button
                        type="button"
                        className={styles.input}
                        disabled={sendingPasswordReset}
                        onClick={() => void handlePasswordReset()}
                      >
                        {sendingPasswordReset
                          ? 'Enviando recuperação...'
                          : 'Enviar recuperação de acesso'}
                      </button>
                      {passwordResetMessage && (
                        <p role="status" className={styles.formSuccess}>
                          {passwordResetMessage}
                        </p>
                      )}
                      {passwordResetError && <p role="alert">{passwordResetError}</p>}
                    </>
                  )}
                </section>

                <section className={styles.formSection} aria-labelledby="sec-endereco">
                  <h3 id="sec-endereco" className={styles.sectionHeading}>
                    Endereço
                  </h3>
                  <div className={styles.fieldGrid}>
                    <TextField
                      id="ec-street"
                      label="Rua"
                      name="street"
                      defaultValue={data.address.street}
                      fieldClassName={styles.fieldFull}
                      placeholder="Rua"
                      autoComplete="street-address"
                    />
                    <TextField
                      id="ec-number"
                      label="Número"
                      name="number"
                      defaultValue={data.address.number}
                    />
                    <TextField
                      id="ec-complement"
                      label="Complemento"
                      name="complement"
                      defaultValue={data.address.complement}
                    />
                    <TextField
                      id="ec-neighborhood"
                      label="Bairro"
                      name="neighborhood"
                      defaultValue={data.address.neighborhood}
                    />
                    <TextField
                      id="ec-city"
                      label="Cidade"
                      name="city"
                      defaultValue={data.address.city}
                      autoComplete="address-level2"
                    />
                    <TextField
                      id="ec-state"
                      label="Estado"
                      name="state"
                      defaultValue={data.address.state}
                      autoComplete="address-level1"
                    />
                    <TextField
                      id="ec-cep"
                      label="CEP"
                      name="postal_code"
                      defaultValue={data.address.postal_code}
                      placeholder="CEP"
                      autoComplete="postal-code"
                    />
                  </div>
                </section>

                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.btnBack}
                    onClick={() => navigate(-1)}
                    disabled={isSavingProfile}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className={styles.btnSubmit}
                    disabled={isSavingProfile}
                  >
                    {isSavingProfile ? 'Salvando…' : 'Salvar alterações'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
